import React, { useState, useEffect } from 'react';
import { database, db } from '../firebase';
import { ref, onValue, get, set, serverTimestamp } from 'firebase/database';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { NavalConstants } from '../constants/NavalConstants';

const RTDB_TO_FIRESTORE_HIERARCHY = {
    '0': 'MRO', '1': 'CBO', '2': '3RM', '3': '2DM', '4': '1RM', '5': 'TTE.CORB.', '6': 'TTE.FRAG.', '7': 'TTE.NAV.', '8': 'CAP.CORB.'
};

const RTDB_TO_FIRESTORE_CORPS = {
    '0': 'CG', '1': 'IM', '2': 'AN', '3': 'MN'
};

const RTDB_TO_FIRESTORE_SERVICES = {
    '0': 'SAIN', '1': 'SAN', '2': 'ING', '3': 'JUS', '4': 'DOC', '5': 'TS', '6': 'MUS', '7': 'SG'
};

const SPECIALTY_NAME_TO_CODE = {
    'Oficinista': 'OFTA', 'Oficinistas': 'OFTA', 'Infanteria de Marina': 'IM', 'Lavandería': 'LAVD', 'Cocina': 'COCN',
    'Armamento': 'ARMM', 'Navegación': 'NAVG', 'Condestable': 'COND', 'Mecánico': 'MEC', 'Electricista': 'ELEC',
    'Electrónica': 'ELTC', 'Torpedos': 'TORP', 'Comunicaciones': 'COM', 'Radar': 'RAD', 'Sonar': 'SON',
    'Contramedidas': 'CMED', 'Piloto Aviador Naval': 'PAN', 'Observador Aéreo': 'OAN', 'Técnico Aeronáutico': 'TAN',
    'Fusilero': 'FUS', 'Artillero': 'ART', 'Médico': 'MED', 'Enfermero': 'ENF', 'Odontólogo': 'ODO', 'Farmacéutico': 'FAR',
    'Médicos Cirujanos': 'MED_CIR', 'Cirujanos Dentistas': 'CIR_DENT', 'Enfermería': 'ENF', 'Psicología': 'PSIC',
    'Nutrición': 'NUTR', 'Sanidad': 'SAN', 'Servicios Generales de Sanidad': 'SGS', 'Químico': 'QUIM',
    'Dentista': 'DENT', 'Radiologo': 'RADIO', 'Laboratorista': 'LAB', 'Licenciada en Pedagogia': 'PEDAG',
    'Licenciada en Educacion Preescolar': 'EDUC_PRE', 'Licenciada en Educacion Primaria': 'EDUC_PRIM',
    'Alimentación': 'ALIM', 'Servicios Generales': 'SG', 'Servicio de Trabajo Social Naval': 'TSN',
    'Costurero': 'COSTR', 'Sastre': 'SSTR', 'Chofer': 'CHOFR', 'Lavandero': 'LAVDR', 'Jardinero': 'JRDNR',
    'Servicios Generales de alimentacion': 'SGA', 'impresor': 'IMPR', 'Camarero': 'CMRR', 'Cocinero': 'COCN',
    'Panadero': 'PNDR', 'Peluquero': 'PLQR', 'Armamento Aereo': 'ARMM_AER', 'Mecánico de Aviación': 'MEC_AVI',
    'Electrónico de Aviación': 'ELE_AVI', 'Operaciones Aereas': 'OPE_AER', 'Controlador de Vuelo': 'CTRL_VLO',
    'Contramaestres': 'CONTRAM', 'Condestables': 'CONDEST', 'Maquinas': 'MAQ', 'Trabajos Submarinos': 'TRAB_SUB',
    'Proteccion Portuaria': 'PROT_PORT', 'Informatico': 'INF', 'Radio Operador': 'RAD_OP', 'Mecánico Automotriz': 'MEC_AUTO',
    'Hidrografía': 'HIDRO', 'Informática/Computación': 'INF_COMP', 'carpintero ': 'CRPNT', 'Tornero': 'TRNR',
    'Soldador': 'SLDR', 'Electromecanico': 'E_MEC', 'Fontanero': 'FONT', 'Criminalistica': 'CRIM',
    'Licenciado en Derecho': 'DER', 'Mecánicos/Motoristas': 'MEC_MOT', 'Electricistas': 'ELEC',
    'Frigoríficos': 'FRIG', 'Control de Averías': 'CTRL_AV', 'Electrónicos': 'ELECTRON',
    'camillero ': 'CAMILL'
};

// Convierte una pregunta de la Bóveda Global (Firestore) al formato RTDB de especialidad.
// Soporta { question, options[], correctAnswer, explanation } y { pregunta, respuestas[]/{} }.
const normalizeVaultQuestion = (q, qIdx) => {
    const qId = qIdx + 1;
    const isCorrect = (value) => (
        value === true || value === 1 || String(value) === "1" || String(value).toLowerCase() === "true"
    );

    let respuestas = [];
    const rawRespuestas = q.respuestas || q.options || q.opciones || [];

    if (Array.isArray(rawRespuestas)) {
        respuestas = rawRespuestas.map((opt, optIdx) => {
            const rId = (qId * 10) + optIdx;
            if (typeof opt === 'string') {
                return {
                    id: rId,
                    respuesta: opt,
                    correcta: (Number(optIdx) === Number(q.correctAnswer)) || (opt === q.respuesta) || (opt === q.respuesta_correcta)
                };
            }
            return {
                id: rId,
                respuesta: opt.respuesta || opt.texto || opt.text || 'Sin respuesta',
                correcta: isCorrect(opt.correcta)
            };
        });
    } else if (rawRespuestas && typeof rawRespuestas === 'object') {
        respuestas = Object.values(rawRespuestas).map((opt, optIdx) => {
            const rId = (qId * 10) + optIdx;
            return {
                id: rId,
                respuesta: opt.respuesta || opt.texto || opt.text || 'Sin respuesta',
                correcta: isCorrect(opt.correcta)
            };
        });
    }

    // Si no se detectó ninguna correcta y hay correctAnswer/options, marcar por índice
    if (!respuestas.some(r => r.correcta) && Array.isArray(rawRespuestas) && q.correctAnswer !== undefined) {
        const ci = parseInt(q.correctAnswer);
        if (!isNaN(ci) && respuestas[ci]) respuestas[ci].correcta = true;
    }

    return {
        id: qId,
        pregunta: q.pregunta || q.question || 'Sin pregunta',
        respuestas,
        explicacion: q.explicacion || q.explanation || ''
    };
};

const AdminAssignVault = () => {
    // Metadata (dinámico desde RTDB)
    const [hierarchyOptions, setHierarchyOptions] = useState([]);
    const [corpsOptions, setCorpsOptions] = useState([]);
    const [specialtiesByCorps, setSpecialtiesByCorps] = useState({});

    // Bóveda Global
    const [vaultQuizzes, setVaultQuizzes] = useState([]);
    const [loadingVault, setLoadingVault] = useState(false);
    const [selectedVaultId, setSelectedVaultId] = useState('');

    // Destino (jerarquía / cuerpo / especialidad / tema)
    const [destJerarquia, setDestJerarquia] = useState('');
    const [destCuerpo, setDestCuerpo] = useState('');
    const [destEspecialidad, setDestEspecialidad] = useState('');
    const [destTemas, setDestTemas] = useState([]);
    const [destTemaId, setDestTemaId] = useState('');
    const [loadingTemas, setLoadingTemas] = useState(false);

    // Datos del cuestionario a publicar
    const [quizzId, setQuizzId] = useState('');
    const [quizzTitulo, setQuizzTitulo] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [status, setStatus] = useState('');

    const GRADOS_LIST = hierarchyOptions.length > 0 ? hierarchyOptions.map(h => h.name) : NavalConstants.GRADOS;
    const CUERPOS_LIST = corpsOptions.length > 0 ? corpsOptions.map(c => c.name) : NavalConstants.CUERPOS_Y_SERVICIOS;

    const getSpecialtiesForCuerpo = (cuerpoName) => {
        if (specialtiesByCorps[cuerpoName]) return specialtiesByCorps[cuerpoName];
        return NavalConstants.ESPECIALIDADES[cuerpoName] || [];
    };

    useEffect(() => {
        const metadataRef = ref(database, 'app_metadata');
        const unsub = onValue(metadataRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.val();

            const corpsList = [];
            if (data.cuerpos_y_servicios) {
                Object.entries(data.cuerpos_y_servicios).forEach(([index, name]) => {
                    const idx = parseInt(index);
                    let firestoreCode, type;
                    if (idx >= 0 && idx <= 3) {
                        firestoreCode = RTDB_TO_FIRESTORE_CORPS[index] || `CG${index}`;
                        type = 'cuerpo';
                    } else {
                        const serviceIdx = (idx - 4).toString();
                        firestoreCode = RTDB_TO_FIRESTORE_SERVICES[serviceIdx] || `SRV${index}`;
                        type = 'servicio';
                    }
                    corpsList.push({ code: firestoreCode, name, type, rtdbIndex: index });
                });
            }
            setCorpsOptions(corpsList);

            if (data.grados) {
                const hierarchies = Object.entries(data.grados).map(([index, value]) => {
                    const firestoreCode = RTDB_TO_FIRESTORE_HIERARCHY[index] || `GRD${index}`;
                    return { code: firestoreCode, name: value, rtdbIndex: index };
                });
                setHierarchyOptions(hierarchies);
            }

            const specialtiesMap = {};
            if (data.especialidades) {
                Object.entries(data.especialidades).forEach(([name, specs]) => {
                    if (Array.isArray(specs)) specialtiesMap[name] = specs;
                });
            }
            setSpecialtiesByCorps(specialtiesMap);
        });
        return () => unsub();
    }, []);

    // Cargar la Bóveda Global (Firestore shared_vault, tipo general/general_promoted)
    const handleFetchVault = async () => {
        setLoadingVault(true);
        setStatus('');
        try {
            const q = query(
                collection(db, 'shared_vault'),
                where('tipo', 'in', ['general', 'general_promoted'])
            );
            const snapshot = await getDocs(q);
            const quizzes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            quizzes.sort((a, b) => {
                const ta = a.fecha_creacion?.toDate?.() || new Date(0);
                const tb = b.fecha_creacion?.toDate?.() || new Date(0);
                return tb - ta;
            });
            setVaultQuizzes(quizzes);
            if (quizzes.length === 0) setStatus('⚠️ No hay cuestionarios en la Bóveda Global (tipo general/general_promoted).');
        } catch (e) {
            setStatus('❌ Error al cargar la Bóveda Global: ' + e.message);
        } finally {
            setLoadingVault(false);
        }
    };

    const selectedQuiz = vaultQuizzes.find(q => q.id === selectedVaultId) || null;

    const handleSelectVault = (quiz) => {
        setSelectedVaultId(quiz.id);
        setQuizzTitulo(quiz.titulo || quiz.name || 'Cuestionario');
        const count = quiz.preguntas?.length || quiz.questionCount || 0;
        const suggestedId = count > 0 ? `Q${count}` : '';
        setQuizzId(suggestedId);
    };

    // Cargar temas existentes de la combinación jerarquía/cuerpo/especialidad
    useEffect(() => {
        if (!destJerarquia || !destCuerpo || !destEspecialidad) {
            setDestTemas([]);
            setDestTemaId('');
            return undefined;
        }
        let cancelled = false;
        setLoadingTemas(true);
        get(ref(database, `especialidades_temas/${destJerarquia}/${destCuerpo}/${destEspecialidad}/temas`))
            .then((snap) => {
                if (cancelled) return;
                if (snap.exists()) {
                    const temas = Object.entries(snap.val()).map(([id, t]) => ({
                        id,
                        nombre: t?.nombre || 'Sin nombre',
                        quizCount: Object.keys(t?.cuestionarios || {}).length
                    }));
                    setDestTemas(temas);
                } else {
                    setDestTemas([]);
                }
            })
            .catch(() => { if (!cancelled) setDestTemas([]); })
            .finally(() => { if (!cancelled) setLoadingTemas(false); });
        return () => { cancelled = true; };
    }, [destJerarquia, destCuerpo, destEspecialidad]);

    const handlePublish = async () => {
        if (!selectedQuiz) return setStatus('❌ Selecciona un cuestionario de la Bóveda Global.');
        if (!destJerarquia || !destCuerpo || !destEspecialidad || !destTemaId) {
            return setStatus('❌ Completa jerarquía, cuerpo/servicio, especialidad y tema destino.');
        }
        if (!quizzId) return setStatus('❌ Indica el ID del cuestionario.');

        const preguntas = Array.isArray(selectedQuiz.preguntas)
            ? selectedQuiz.preguntas
            : Object.entries(selectedQuiz.preguntas || {}).map(([id, p]) => ({ id, ...p }));
        if (preguntas.length === 0) return setStatus('❌ Este cuestionario no tiene preguntas.');

        const formattedQuestions = preguntas
            .filter(Boolean)
            .map((q, idx) => normalizeVaultQuestion(q, idx));

        const quizzIdNumber = parseInt(quizzId.replace(/[^0-9]/g, '')) || (Date.now() % 100000);

        if (!window.confirm(
            `¿Asignar "${quizzTitulo}" (${formattedQuestions.length} preguntas)?\n\n` +
            `Destino:\nespecialidades_temas/${destJerarquia}/${destCuerpo}/${destEspecialidad}/temas/${destTemaId}/cuestionarios/${quizzIdNumber}`
        )) return;

        setPublishing(true);
        setStatus('');
        try {
            const path = `especialidades_temas/${destJerarquia}/${destCuerpo}/${destEspecialidad}/temas/${destTemaId}/cuestionarios/${quizzIdNumber}`;
            await set(ref(database, path), {
                id: quizzIdNumber,
                titulo: quizzTitulo,
                preguntas: formattedQuestions,
                publicado: serverTimestamp(),
                origen: 'boveda_global'
            });
            setStatus(`✅ Cuestionario asignado correctamente (${formattedQuestions.length} preguntas).`);
        } catch (e) {
            setStatus('❌ Error al publicar: ' + e.message);
        } finally {
            setPublishing(false);
        }
    };

    return (
        <div className="admin-quizz-mgmt" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h1 style={{ color: 'var(--accent)', margin: 0 }}>📤 Asignar Bóveda Global a Especialidad</h1>
                <button onClick={() => window.history.back()} className="btn-secondary">← Volver</button>
            </div>

            <p className="hint" style={{ marginBottom: '20px' }}>
                Selecciona un cuestionario de la Bóveda Global (Firestore <code>shared_vault</code>) y asígnalo a una
                jerarquía, cuerpo/servicio y especialidad de <b>SEMAR</b>, reutilizando un tema existente de esa ruta.
            </p>

            <div className="admin-grid" style={{ display: 'grid', gap: '20px' }}>

                {/* PASO 1: Bóveda Global */}
                <div className="admin-card-expandable">
                    <div className="section-header" style={{ padding: '15px' }}>
                        <span style={{ fontSize: '1.4rem', marginRight: '10px' }}>1️⃣</span>
                        <h3 style={{ margin: 0, flex: 1 }}>Seleccionar Cuestionario de la Bóveda Global</h3>
                    </div>
                    <div className="section-body">
                        <div className="vault-controls" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                            <button className="btn-secondary" onClick={handleFetchVault} disabled={loadingVault}>
                                {loadingVault ? 'Cargando...' : '🔄 Cargar Bóveda Global'}
                            </button>
                            <span style={{ fontSize: '0.85rem', color: '#888', alignSelf: 'center' }}>
                                {vaultQuizzes.length > 0 ? `${vaultQuizzes.length} cuestionarios` : ''}
                            </span>
                        </div>

                        {vaultQuizzes.length > 0 && (
                            <div className="shared-list" style={{ maxHeight: '320px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px' }}>
                                {vaultQuizzes.map(q => {
                                    const qty = Array.isArray(q.preguntas) ? q.preguntas.length : Object.keys(q.preguntas || {}).length;
                                    const isSelected = selectedVaultId === q.id;
                                    return (
                                        <div
                                            key={q.id}
                                            onClick={() => handleSelectVault(q)}
                                            style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '6px',
                                                background: isSelected ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.02)',
                                                border: isSelected ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.05)',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <strong style={{ fontSize: '0.85rem' }}>{q.titulo || q.name || 'Sin título'}</strong>
                                                <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                                    {qty} preguntas • {q.tipo || 'general'}
                                                    {q.fecha_creacion?.toDate ? ` • ${q.fecha_creacion.toDate().toLocaleDateString()}` : ''}
                                                </div>
                                            </div>
                                            <span style={{ fontSize: '1.1rem', color: isSelected ? '#3b82f6' : '#666' }}>
                                                {isSelected ? '✔️' : '⚪'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {selectedQuiz && (
                            <div className="status-box" style={{ marginTop: '12px', background: 'rgba(59,130,246,0.08)', padding: '10px', borderRadius: '8px', border: '1px solid #3b82f6', fontSize: '0.85rem' }}>
                                📌 Seleccionado: <b>{quizzTitulo}</b>
                            </div>
                        )}
                    </div>
                </div>

                {/* PASO 2: Destino */}
                <div className="admin-card-expandable">
                    <div className="section-header" style={{ padding: '15px' }}>
                        <span style={{ fontSize: '1.4rem', marginRight: '10px' }}>2️⃣</span>
                        <h3 style={{ margin: 0, flex: 1 }}>Jerarquía, Cuerpo/Servicio y Especialidad (SEMAR)</h3>
                    </div>
                    <div className="section-body">
                        <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="input-group">
                                <label>Jerarquía</label>
                                <select value={destJerarquia} onChange={e => { setDestJerarquia(e.target.value); setDestTemaId(''); }}>
                                    <option value="">Seleccionar...</option>
                                    {GRADOS_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Cuerpo / Servicio</label>
                                <select value={destCuerpo} onChange={e => { setDestCuerpo(e.target.value); setDestEspecialidad(''); setDestTemaId(''); }}>
                                    <option value="">Seleccionar...</option>
                                    {CUERPOS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Especialidad</label>
                                <select value={destEspecialidad} onChange={e => { setDestEspecialidad(e.target.value); setDestTemaId(''); }}>
                                    <option value="">Seleccionar...</option>
                                    <option value="TODOS">TODAS</option>
                                    {getSpecialtiesForCuerpo(destCuerpo).map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Tema destino (existente)</label>
                                {loadingTemas ? (
                                    <select disabled><option>⏳ Cargando temas...</option></select>
                                ) : destTemas.length === 0 ? (
                                    <select disabled>
                                        <option>{destJerarquia && destCuerpo && destEspecialidad ? 'Sin temas en esta ruta' : 'Selecciona jerarquía, cuerpo y especialidad'}</option>
                                    </select>
                                ) : (
                                    <select value={destTemaId} onChange={e => setDestTemaId(e.target.value)}>
                                        <option value="">Seleccionar tema...</option>
                                        {destTemas.map(t => (
                                            <option key={t.id} value={t.id}>#{t.id} {t.nombre} ({t.quizCount} quiz)</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* PASO 3: Publicar */}
                <div className="admin-card-expandable">
                    <div className="section-header" style={{ padding: '15px' }}>
                        <span style={{ fontSize: '1.4rem', marginRight: '10px' }}>3️⃣</span>
                        <h3 style={{ margin: 0, flex: 1 }}>Datos del Cuestionario y Publicación</h3>
                    </div>
                    <div className="section-body">
                        <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="input-group">
                                <label>ID del Cuestionario</label>
                                <input type="text" placeholder="Ej: Q1" value={quizzId} onChange={e => setQuizzId(e.target.value)} />
                            </div>
                            <div className="input-group">
                                <label>Título</label>
                                <input type="text" value={quizzTitulo} onChange={e => setQuizzTitulo(e.target.value)} />
                            </div>
                        </div>

                        {status && (
                            <div style={{
                                marginTop: '15px', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                                background: status.startsWith('✅') ? 'rgba(16,185,129,0.1)' : status.startsWith('❌') ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                                border: '1px solid ' + (status.startsWith('✅') ? '#10b981' : status.startsWith('❌') ? '#ef4444' : '#3b82f6'),
                                color: status.startsWith('✅') ? '#10b981' : status.startsWith('❌') ? '#ef4444' : '#3b82f6',
                            }}>
                                {status}
                            </div>
                        )}

                        <button className="btn-save full-width adm-btn adm-btn--secondary"  style={{  marginTop: '15px'  }} onClick={handlePublish} disabled={publishing}>
                            {publishing ? '⏳ Publicando...' : '🚀 Asignar a Especialidad'}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .admin-quizz-mgmt { color: white; }
                .admin-card-expandable { background: var(--bg-secondary); border-radius: 15px; overflow: hidden; border: 1px solid rgba(255,255,255,0.05); margin-bottom: 20px; }
                .section-header { background: rgba(255,255,255,0.02); border-bottom: 1px solid rgba(255,255,255,0.05); display: flex; align-items: center; }
                .section-body { padding: 20px; }
                .input-group { display: flex; flex-direction: column; gap: 5px; }
                .input-group label { font-size: 0.8rem; font-weight: bold; color: var(--accent); }
                .input-group input, .input-group select { padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white; width: 100%; }
                .btn-save.full-width { width: 100%; }
                @media (max-width: 768px) { .form-grid-2 { grid-template-columns: 1fr; } }
            `}</style>
        </div>
    );
};

export default AdminAssignVault;
